// Handles for shops and @usernames for people share one namespace, like
// Instagram: 3–30 characters of a–z, 0–9, "." and "_", case-insensitive
// (stored lower case), no leading, trailing or double dots. Nothing is
// numbered automatically: people choose. When a handle changes, the old one
// keeps redirecting for 30 days and is then free for anyone.

export const HANDLE_MIN_LENGTH = 3;
export const HANDLE_MAX_LENGTH = 30;
export const HANDLE_REDIRECT_DAYS = 30;
export const HANDLE_CHANGE_COOLDOWN_DAYS = 14;
const DAY_MS = 24 * 60 * 60 * 1000;

// Words that would read as Guildite itself or collide with app paths.
export const RESERVED_HANDLES: ReadonlySet<string> = new Set([
  'admin', 'administrator', 'api', 'app', 'auth', 'cart', 'checkout', 'create', 'discover', 'explore', 'feed', 'g',
  'guild', 'guilds', 'guildite', 'guildite.official', 'guilditeofficial', 'guildite_official', 'help', 'home', 'login',
  'logout', 'map', 'me', 'messages', 'moderator', 'new', 'notifications', 'official', 'orders', 'p', 'payouts',
  'privacy', 'refunds', 'rider', 'riders', 'root', 's', 'saved', 'security', 'settings', 'shop', 'shops', 'signin',
  'signup', 'staff', 'support', 'system', 'team', 'terms', 'u', 'verify', 'www',
]);

export type HandleProblem = 'too_short' | 'too_long' | 'characters' | 'dots' | 'reserved';

export const HANDLE_PROBLEM_MESSAGES: Record<HandleProblem, string> = {
  too_short: `Use at least ${HANDLE_MIN_LENGTH} characters.`,
  too_long: `Use ${HANDLE_MAX_LENGTH} characters or fewer.`,
  characters: 'Use only letters, numbers, full stops and underscores.',
  dots: 'A full stop can’t come first, last or twice in a row.',
  reserved: 'That name is reserved. Try another.',
};

// What people type or paste: " @Barrel.Bean " → "barrel.bean".
export function normalizeHandleInput(value: unknown): string {
  return String(value ?? '').trim().replace(/^@+/, '').toLowerCase();
}

export function handleProblem(handle: string): HandleProblem | null {
  if (handle.length < HANDLE_MIN_LENGTH) return 'too_short';
  if (handle.length > HANDLE_MAX_LENGTH) return 'too_long';
  if (!/^[a-z0-9._]+$/.test(handle)) return 'characters';
  if (handle.startsWith('.') || handle.endsWith('.') || handle.includes('..')) return 'dots';
  if (RESERVED_HANDLES.has(handle)) return 'reserved';
  return null;
}

export function isValidHandle(value: unknown): value is string {
  return typeof value === 'string' && value === value.toLowerCase() && handleProblem(value) === null;
}

export type HandleCheck =
  | { ok: true; handle: string }
  | { ok: false; handle: string; problem: HandleProblem; message: string };

export function checkHandleFormat(input: unknown): HandleCheck {
  const handle = normalizeHandleInput(input);
  const problem = handleProblem(handle);
  return problem ? { ok: false, handle, problem, message: HANDLE_PROBLEM_MESSAGES[problem] } : { ok: true, handle };
}

// A starting suggestion from a name: "Barrel & Bean" → "barrelandbean".
// Only a suggestion; the person confirms or edits it.
export function suggestHandle(name: unknown): string {
  return String(name ?? '')
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]/g, '')
    .slice(0, HANDLE_MAX_LENGTH);
}

export type HandleOwner = { type: 'shop' | 'user'; id: string };

// handles/{handle}: the current handle of a shop or person, or an old one that
// still redirects until redirectUntil.
export type HandleRecord = {
  type: 'shop' | 'user';
  id: string;
  primary: boolean;
  redirectUntil?: string | null;
  createdAt?: string;
};

export function isHandleRecord(value: unknown): value is HandleRecord {
  const record = value as Record<string, unknown> | null;
  return Boolean(record) && (record?.type === 'shop' || record?.type === 'user') && typeof record?.id === 'string' && typeof record?.primary === 'boolean';
}

// Taken while it is someone's current handle, or while an old handle still
// redirects, except for its previous owner, who may take it back.
export function handleAvailableFor(record: unknown, claimant: HandleOwner, now: number): boolean {
  if (!isHandleRecord(record)) return true;
  if (record.type === claimant.type && record.id === claimant.id) return true;
  if (record.primary) return false;
  const until = Date.parse(String(record.redirectUntil || ''));
  return !Number.isFinite(until) || until <= now;
}

// Where an address should lead: the owner while the record is current or
// still redirecting; nobody once an old handle has expired.
export function handleTarget(record: unknown, now: number): HandleOwner | null {
  if (!isHandleRecord(record)) return null;
  if (record.primary) return { type: record.type, id: record.id };
  const until = Date.parse(String(record.redirectUntil || ''));
  return Number.isFinite(until) && until > now ? { type: record.type, id: record.id } : null;
}

export function redirectUntil(now: number): string {
  return new Date(now + HANDLE_REDIRECT_DAYS * DAY_MS).toISOString();
}

export function nextHandleChangeAt(lastChangedAt: unknown): number | null {
  const at = Date.parse(String(lastChangedAt || ''));
  return Number.isFinite(at) ? at + HANDLE_CHANGE_COOLDOWN_DAYS * DAY_MS : null;
}
